// Items.styles.ts
import styled, { keyframes } from "styled-components";

// ===== ANIMAÇÕES =====
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;
  animation: ${fadeIn} 0.5s ease;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.colors.selectionDark};
    cursor: grab;
  }

  &::-webkit-scrollbar-thumb:active {
    cursor: grabbing;
  }

  &:hover {
    scrollbar-width: auto;
  }

  scrollbar-color: ${(props) => props.theme.colors.selection};
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--sp-24);
  flex-wrap: wrap;
  gap: var(--sp-16);
  animation: ${slideInFromLeft} 0.6s ease;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-8);
`;

export const HeaderTitle = styled.h1`
  font-size: calc(var(--u) * 28);
  font-weight: 700;
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: var(--sp-12);

  @media (max-width: 768px) {
    font-size: var(--fs-2xl);
  }
`;

export const HeaderSubtitle = styled.p`
  color: ${(props) => props.theme.colors.label};
  margin: 0;
  font-size: var(--fs-md);
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: var(--sp-12);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

// ===== FILTROS =====
export const FilterSection = styled.div`
  display: flex;
  gap: var(--sp-12);
  margin-bottom: var(--sp-24);
  flex-wrap: wrap;
  animation: ${slideInFromRight} 0.6s ease;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: calc(var(--u) * 300);

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  left: calc(var(--u) * 12);
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => props.theme.colors.label};
  pointer-events: none;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: var(--sp-12) var(--sp-40) var(--sp-12) var(--sp-40);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
  }
`;

export const ClearButton = styled.button`
  position: absolute;
  right: calc(var(--u) * 12);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.label};
  cursor: pointer;
  padding: var(--sp-4);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.border};
    color: ${(props) => props.theme.colors.text};
  }
`;

export const FilterSelect = styled.select`
  padding: var(--sp-12) var(--sp-16);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: calc(var(--u) * 180);

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

// ===== ESTATÍSTICAS =====
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 200), 1fr));
  gap: var(--sp-16);
  margin-bottom: var(--sp-24);
  animation: ${scaleIn} 0.6s ease;
`;

export const StatCard = styled.div<{ $color?: string }>`
  padding: var(--sp-20);
  background: linear-gradient(
    135deg,
    ${(props) => props.$color || props.theme.colors.selection}15 0%,
    ${(props) => props.$color || props.theme.colors.selection}08 100%
  );
  border: 2px solid ${(props) => props.$color || props.theme.colors.selection}30;
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px
      ${(props) => props.$color || props.theme.colors.selection}20;
    border-color: ${(props) => props.$color || props.theme.colors.selection}50;
  }
`;

export const StatIcon = styled.div`
  font-size: var(--fs-4xl);
  margin-bottom: var(--sp-8);
  animation: ${bounce} 2s ease-in-out infinite;
`;

export const StatValue = styled.div`
  font-size: calc(var(--u) * 28);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-4);
`;

export const StatLabel = styled.div`
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
  font-weight: 600;
`;

// ===== TUTORIAL/GUIA =====
export const WelcomeGuide = styled.div`
  background: linear-gradient(135deg, #e7f3ff 0%, #f0f7ff 100%);
  border: 2px solid #b3d9ff;
  border-radius: 12px;
  padding: var(--sp-24);
  margin-bottom: var(--sp-24);
  position: relative;
  animation: ${slideInFromLeft} 0.7s ease;
`;

export const WelcomeCloseButton = styled.button`
  position: absolute;
  top: calc(var(--u) * 12);
  right: calc(var(--u) * 12);
  background: none;
  border: none;
  font-size: var(--fs-2xl);
  cursor: pointer;
  color: #0066cc;
  width: calc(var(--u) * 32);
  height: calc(var(--u) * 32);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 102, 204, 0.1);
    transform: rotate(90deg);
  }
`;

export const WelcomeContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--sp-16);
`;

export const WelcomeIcon = styled.div`
  background-color: #0066cc;
  color: white;
  border-radius: 50%;
  width: calc(var(--u) * 48);
  height: calc(var(--u) * 48);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const WelcomeText = styled.div`
  flex: 1;

  h3 {
    margin: 0 0 var(--sp-8) 0;
    color: #0066cc;
    font-size: var(--fs-xl);
  }

  p {
    margin: 0 0 var(--sp-16) 0;
    color: #0066cc;
    font-size: var(--fs-md);
    line-height: 1.5;
  }
`;

export const WelcomeTipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 200), 1fr));
  gap: var(--sp-12);
`;

export const WelcomeTip = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-sm);
  color: #0066cc;

  span:first-child {
    flex-shrink: 0;
  }
`;

// ===== VIEW MODE TOGGLE =====
export const ViewModeToggle = styled.div`
  display: flex;
  gap: var(--sp-4);
  background-color: ${(props) => props.theme.colors.background};
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  padding: var(--sp-4);
`;

export const ViewModeButton = styled.button<{ $isActive: boolean }>`
  padding: var(--sp-8) var(--sp-16);
  border: none;
  background-color: ${(props) =>
    props.$isActive ? props.theme.colors.selection : "transparent"};
  color: ${(props) =>
    props.$isActive ? props.theme.colors.text : props.theme.colors.label};
  border-radius: 6px;
  cursor: pointer;
  font-size: var(--fs-sm);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: var(--sp-6);
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.$isActive
        ? props.theme.colors.selection
        : props.theme.colors.border};
  }
`;

// ===== CARDS DE ITEM =====
export const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(calc(var(--u) * 320), 1fr));
  gap: var(--sp-20);
  animation: ${scaleIn} 0.6s ease;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ItemCard = styled.div`
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 12px;
  padding: var(--sp-20);
  background-color: ${(props) => props.theme.colors.background};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  animation: ${fadeIn} 0.5s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${(props) => props.theme.colors.selection};
  }
`;

// ===== HIERARQUIA =====
export const HierarchySection = styled.div<{ $level: number }>`
  margin-left: ${(props) => props.$level * 32}px;
  margin-top: ${(props) => (props.$level > 0 ? "var(--sp-16)" : "0")};
  border-left: ${(props) =>
    props.$level > 0 ? `2px solid ${props.theme.colors.border}` : "none"};
  padding-left: ${(props) => (props.$level > 0 ? "var(--sp-32)" : "0")};
  animation: ${slideInFromLeft} ${(props) => 0.4 + props.$level * 0.1}s ease;
`;

export const HierarchyHeader = styled.div<{
  $expanded: boolean;
  $level: number;
}>`
  background: ${(props) => {
    if (props.$level === 0)
      return props.$expanded ? "#e7f3ff" : props.theme.colors.background + "80";
    if (props.$level === 1)
      return props.$expanded ? "#f0f7ff" : props.theme.colors.background;
    return props.$expanded ? "#f8f9fa" : props.theme.colors.background;
  }};
  padding: ${(props) =>
    props.$level === 0 ? "var(--sp-20)" : props.$level === 1 ? "var(--sp-16)" : "calc(var(--u) * 14)"};
  border-radius: ${(props) =>
    props.$level === 0 ? "12px" : props.$level === 1 ? "8px" : "6px"};
  border: 2px solid
    ${(props) => {
      if (props.$level === 0)
        return props.$expanded ? "#b3d9ff" : props.theme.colors.border;
      if (props.$level === 1) return props.$expanded ? "#cce5ff" : "#f1f3f4";
      return props.$expanded ? "#dee2e6" : "#f8f9fa";
    }};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  margin-bottom: ${(props) => (props.$level === 0 ? "var(--sp-20)" : "0")};

  &:hover {
    transform: translateX(4px);
  }
`;

export const HierarchyIcon = styled.div<{ $level: number; $expanded: boolean }>`
  background-color: ${(props) => {
    if (props.$level === 0) return props.$expanded ? "#0066cc" : "#6c757d";
    if (props.$level === 1) return props.$expanded ? "#4dabf7" : "#adb5bd";
    return props.$expanded ? "#74c0fc" : "#ced4da";
  }};
  color: white;
  border-radius: ${(props) =>
    props.$level === 0 ? "8px" : props.$level === 1 ? "6px" : "4px"};
  width: ${(props) =>
    props.$level === 0 ? "calc(var(--u) * 48)" : props.$level === 1 ? "calc(var(--u) * 40)" : "calc(var(--u) * 36)"};
  height: ${(props) =>
    props.$level === 0 ? "calc(var(--u) * 48)" : props.$level === 1 ? "calc(var(--u) * 40)" : "calc(var(--u) * 36)"};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
`;

export const HierarchyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
`;

export const HierarchyTitle = styled.h3<{ $level: number }>`
  margin: 0 0 var(--sp-4) 0;
  font-size: ${(props) =>
    props.$level === 0 ? "var(--fs-xl)" : props.$level === 1 ? "var(--fs-base)" : "var(--fs-md)"};
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const HierarchyMeta = styled.div`
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
  display: flex;
  align-items: center;
  gap: var(--sp-12);
`;

export const ExpandIcon = styled.span<{ $expanded: boolean }>`
  transition: transform 0.3s ease;
  transform: ${(props) =>
    props.$expanded ? "rotate(180deg)" : "rotate(0deg)"};
`;

// ===== LOADING =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: calc(var(--u) * 60) var(--sp-20);
  gap: var(--sp-16);
  color: ${(props) => props.theme.colors.label};
`;

export const LoadingSpinner = styled.div`
  width: calc(var(--u) * 48);
  height: calc(var(--u) * 48);
  border: 4px solid ${(props) => props.theme.colors.border};
  border-top-color: ${(props) => props.theme.colors.selection};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

// ===== EMPTY STATE =====
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: calc(var(--u) * 80) var(--sp-40);
  text-align: center;
  background-color: ${(props) => props.theme.colors.background + "80"};
  border-radius: 12px;
  border: 2px dashed ${(props) => props.theme.colors.border};
  animation: ${fadeIn} 0.6s ease;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-7xl);
  margin-bottom: var(--sp-16);
  opacity: 0.5;
  animation: ${bounce} 2s ease-in-out infinite;
`;

export const EmptyStateTitle = styled.h3`
  margin: 0 0 var(--sp-8) 0;
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-xl);
`;

export const EmptyStateDescription = styled.p`
  margin: 0 0 var(--sp-24) 0;
  color: ${(props) => props.theme.colors.label};
  font-size: var(--fs-md);
`;

// ===== BOTÕES =====
export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-12) var(--sp-24);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-8);
  min-width: calc(var(--u) * 140);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px
      ${(props) => props.color || props.theme.colors.selection}40;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-12) var(--sp-24);
  background-color: transparent;
  color: ${(props) => props.theme.colors.text};
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  font-size: var(--fs-md);
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-8);

  &:hover {
    background-color: ${(props) => props.theme.colors.background};
    border-color: ${(props) => props.theme.colors.text + "40"};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// ===== INPUT =====
export const Input = styled.input`
  width: 100%;
  padding: var(--sp-12) calc(var(--u) * 14);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// ===== ÍCONES =====
export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

// ===== CONTADOR DE RESULTADOS =====
export const ResultsCounter = styled.div`
  text-align: center;
  padding: var(--sp-16);
  color: ${(props) => props.theme.colors.label};
  font-size: var(--fs-md);
  margin-top: var(--sp-24);
  animation: ${fadeIn} 0.5s ease;

  strong {
    color: ${(props) => props.theme.colors.selection};
    font-weight: 700;
  }
`;

// Exportação padrão
export default {
  FieldsContainer,
  Header,
  HeaderContent,
  HeaderTitle,
  HeaderSubtitle,
  HeaderActions,
  FilterSection,
  SearchWrapper,
  SearchIcon,
  SearchInput,
  ClearButton,
  FilterSelect,
  StatsGrid,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  WelcomeGuide,
  WelcomeCloseButton,
  WelcomeContent,
  WelcomeIcon,
  WelcomeText,
  WelcomeTipsGrid,
  WelcomeTip,
  ViewModeToggle,
  ViewModeButton,
  ItemsGrid,
  ItemCard,
  HierarchySection,
  HierarchyHeader,
  HierarchyIcon,
  HierarchyInfo,
  HierarchyTitle,
  HierarchyMeta,
  ExpandIcon,
  LoadingContainer,
  LoadingSpinner,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  Button,
  SecondaryButton,
  Input,
  MaterialIcons,
  ResultsCounter,
};
